import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Map, Users, Phone, Video, LogOut, Target, DollarSign, BarChart3, Trophy, Settings, Shield, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/onboarding", label: "Onboarding", icon: Map, id: "nav-onboarding" },
  { to: "/leads", label: "Pipeline", icon: Users, id: "nav-leads" },
  { to: "/dialer", label: "Dialer", icon: Phone, id: "nav-dialer" },
  { to: "/war-room", label: "War Room", icon: Video, id: "nav-warroom" },
  { to: "/commissions", label: "Commissions", icon: DollarSign, id: "nav-commissions" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, id: "nav-analytics" },
  { to: "/goals", label: "Goals", icon: Target, id: "nav-goals" },
];

const adminNav = [
  { to: "/admin/team", label: "Team", icon: Shield, id: "nav-admin-team" },
  { to: "/admin/leaderboard", label: "Leaderboard", icon: Trophy, id: "nav-admin-leaderboard" },
  { to: "/admin/settings", label: "Admin Settings", icon: Settings, id: "nav-admin-settings" },
];

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [time, setTime] = useState(new Date());
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const handleLogout = () => { logout(); navigate("/login"); };
  const closeMenu = () => setMenuOpen(false);

  const Sidebar = (
    <aside className="w-60 border-r border-term flex flex-col h-full" style={{ background: "var(--bg-raised)" }} data-testid="app-sidebar">
      <div className="flex items-center justify-between px-5 py-5 border-b border-term">
        <Link to="/dashboard" onClick={closeMenu} className="flex-1">
          <div className="font-display text-xl leading-none text-amber">COYOTE/LIFE</div>
          <div className="label-xs mt-2 text-fg-subtle">Recruiting OS</div>
        </Link>
        <button onClick={closeMenu} className="lg:hidden text-fg-muted p-1" data-testid="sidebar-close"><X size={18} /></button>
      </div>

      <nav className="flex-1 py-2 overflow-y-auto">
        {nav.map((item) => (
          <NavLink
            key={item.to} to={item.to} onClick={closeMenu}
            data-testid={item.id}
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-2.5 text-xs font-mono uppercase tracking-widest border-l-2 transition-all ${
                isActive ? "border-amber text-amber" : "border-transparent text-fg-muted hover:text-fg"
              }`
            }
            style={({ isActive }) => isActive ? { background: "rgba(255, 59, 71, 0.08)" } : {}}
          >
            <item.icon size={14} strokeWidth={1.75} />
            {item.label}
          </NavLink>
        ))}

        {user?.role === "admin" && (
          <>
            <div className="px-5 pt-6 pb-2 label-xs text-electric">◆ ADMIN</div>
            {adminNav.map((item) => (
              <NavLink
                key={item.to} to={item.to} onClick={closeMenu}
                data-testid={item.id}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2.5 text-xs font-mono uppercase tracking-widest border-l-2 transition-all ${
                    isActive ? "border-electric text-electric" : "border-transparent text-fg-muted hover:text-fg"
                  }`
                }
                style={({ isActive }) => isActive ? { background: "rgba(77, 158, 255, 0.08)" } : {}}
              >
                <item.icon size={14} strokeWidth={1.75} />
                {item.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="border-t border-term p-4">
        <div className="label-xs mb-1.5">Agent</div>
        <div className="text-xs font-mono text-fg truncate" data-testid="sidebar-user-name">{user?.full_name}</div>
        <div className="text-[10px] font-mono text-fg-subtle truncate mb-3">{user?.email}</div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] py-2 border border-strong text-fg-muted hover:border-live hover:text-live transition-all rounded"
        >
          <LogOut size={12} /> Sign Out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg)" }}>
      {/* Top status bar */}
      <div className="h-9 border-b border-term flex items-center justify-between px-3 sm:px-4 text-[10px] font-mono text-fg-muted tracking-widest uppercase" style={{ background: "var(--bg-raised)" }} data-testid="top-status-bar">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <button
            onClick={() => setMenuOpen(true)}
            className="lg:hidden text-electric p-1 -ml-1"
            data-testid="menu-toggle"
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>
          <span className="hidden sm:flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-gain rounded-full pulse-live"></span><span className="text-gain">SYS ONLINE</span></span>
          <span className="hidden sm:inline text-fg-subtle">|</span>
          <span className="text-amber truncate">COYOTE LIFE / CRM</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          {user?.role === "admin" && <span className="text-electric hidden sm:inline">◆ ADMIN</span>}
          <span className="hidden md:inline">{time.toISOString().replace("T", " ").slice(0, 19)} UTC</span>
          <span className="hidden md:inline text-fg-subtle">|</span>
          <span className="truncate max-w-[120px] sm:max-w-none">{user?.full_name}</span>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Desktop sidebar */}
        <div className="hidden lg:block flex-shrink-0">
          {Sidebar}
        </div>

        {/* Mobile slide-out sidebar */}
        {menuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-menu">
            <div className="absolute inset-0 bg-black/70" onClick={closeMenu} />
            <div className="absolute left-0 top-0 bottom-0 w-60 slide-up">
              {Sidebar}
            </div>
          </div>
        )}

        <main className="flex-1 overflow-auto min-w-0" style={{ background: "var(--bg)" }}>{children}</main>
      </div>
    </div>
  );
}
